/**
 * Les avoirs, et la seule règle qui les gouverne : un avoir se SOUSTRAIT.
 *
 * CE QUI SE PASSAIT. Les colonnes « type », « resolution », « factureOriginaleId »
 * et « factureAppliqueeId » existent en production depuis le 27 mai 2026, posées
 * par deux migrations dont les fichiers n'existent nulle part. Le mot « Avoir »
 * n'apparaissait dans AUCUN fichier de « src/ ». Trois avoirs vivaient donc en
 * base — 34 053,19 € TTC chez Eco Delivery Urban — sans qu'une ligne de code ne
 * sache les distinguer d'une facture :
 *
 *   - la requête du tableau de bord ne SÉLECTIONNAIT même pas la colonne
 *     « type » : l'information n'arrivait jamais jusqu'au calcul ;
 *   - le mappage écrivait « type: "Facture" » en dur sur chaque ligne ;
 *   - tous les agrégats filtraient sur « statut », jamais sur la nature.
 *
 * Résultat : les avoirs étaient ADDITIONNÉS au chiffre d'affaires là où ils
 * devaient en être retranchés. Sur Eco Delivery Urban, 241 350,62 € affichés
 * pour 173 244,24 € réels — 68 106,38 € d'écart, soit deux fois le montant des
 * avoirs.
 *
 * LES QUATRE RÈGLES, décidées le 29 août 2026 :
 *
 * 1. Un avoir se déduit DÈS SON ÉMISSION, indépendamment de sa résolution.
 * 2. Les montants restent POSITIFS en base. Le signe négatif n'existe que dans
 *    les calculs — « montantTTCSigne » et lui seul. Aucune écriture ne renverse
 *    un montant : c'est réversible, et une base ne ment pas.
 * 3. « resolution » ne dit qu'une chose : reste-t-il à rembourser ou à imputer.
 *    Elle n'entre JAMAIS dans le chiffre d'affaires. Un avoir résolu et un avoir
 *    en attente pèsent exactement pareil.
 * 4. Un avoir n'est JAMAIS une recette — pas même passé à « Payée ». « Payée »
 *    sur un avoir signifie « remboursé au client », c'est-à-dire une sortie.
 *    C'est « estRecette » qui tient cette règle, et rien d'autre.
 */

export type NatureDocument = 'Facture' | 'Avoir';

/** Ce que porte la colonne « type » en base pour un avoir. */
export const NATURE_AVOIR: NatureDocument = 'Avoir';

/** Un avoir non résolu reste à rembourser ou à imputer. */
export const RESOLUTION_EN_ATTENTE = 'En attente';

export interface DocumentComptable {
  /** La colonne « type » de la table « Facture » : « Facture » ou « Avoir ». */
  nature?: string | null;
  statut?: string | null;
  totalHT?: number | null;
  totalTTC?: number | null;
  resolution?: string | null;
  /** L'échéance — « echeance » dans l'interface, « dateEcheance » en base. */
  echeance?: string | Date | null;
  dateEcheance?: string | Date | null;
}

function arrondirCentimes(montant: number): number {
  return Math.round(montant * 100) / 100;
}

/**
 * La nature d'un document, « Facture » par défaut.
 *
 * Le défaut n'est pas une commodité : 406 lignes de production portent
 * « Facture », et tout document dont la nature n'a pas été chargée doit être
 * traité comme une facture — jamais comme un avoir. Se tromper dans ce sens
 * laisse un chiffre inchangé ; dans l'autre, il retrancherait une vraie recette.
 */
export function natureDe(doc: DocumentComptable | null | undefined): NatureDocument {
  return doc?.nature === NATURE_AVOIR ? 'Avoir' : 'Facture';
}

export function estAvoir(doc: DocumentComptable | null | undefined): boolean {
  return natureDe(doc) === 'Avoir';
}

/** −1 pour un avoir, +1 pour une facture. Le seul endroit où le signe naît. */
export function signeComptable(doc: DocumentComptable | null | undefined): 1 | -1 {
  return estAvoir(doc) ? -1 : 1;
}

export function montantTTCSigne(doc: DocumentComptable | null | undefined): number {
  return arrondirCentimes((Number(doc?.totalTTC) || 0) * signeComptable(doc));
}

export function montantHTSigne(doc: DocumentComptable | null | undefined): number {
  return arrondirCentimes((Number(doc?.totalHT) || 0) * signeComptable(doc));
}

export function sommeTTCSignee(docs: DocumentComptable[]): number {
  return arrondirCentimes(docs.reduce((somme, d) => somme + montantTTCSigne(d), 0));
}

export function sommeHTSignee(docs: DocumentComptable[]): number {
  return arrondirCentimes(docs.reduce((somme, d) => somme + montantHTSigne(d), 0));
}

/**
 * Ce document est-il une RECETTE ?
 *
 * Un avoir ne l'est jamais, quel que soit son statut. Le passer à « Payée »
 * veut dire qu'il a été remboursé : c'est de l'argent qui sort.
 */
export function estRecette(doc: DocumentComptable | null | undefined): boolean {
  if (!entreDansLActivite(doc)) return false;
  return !estAvoir(doc) && doc?.statut === 'Payée';
}

/**
 * L'ARCHIVAGE, ET CE QU'IL SIGNIFIE ICI.
 *
 * Chez MyFacture, l'archivage intervient APRÈS la mise à la corbeille : il ne
 * sert qu'à conserver une trace. Une facture archivée est sortie de l'activité.
 *
 * Elle n'entre donc dans AUCUN calcul — ni chiffre d'affaires facturé ou
 * encaissé, ni retard, ni impayé, ni somme à encaisser, ni graphique, ni
 * statistique client ou produit — sur mobile comme sur ordinateur. Elle reste
 * consultable dans les archives, avec son historique. Restaurée, elle
 * réintègre les calculs selon son statut réel : ce n'est pas l'archivage qui
 * est mémorisé quelque part, c'est le statut courant qui décide, à chaque fois.
 *
 * Cette règle vaut MÊME POUR UNE FACTURE PAYÉE. UrbanGlobal en a une, de 19 € :
 * elle est à la corbeille, ses 19 € ne comptent pas. C'est la contrepartie
 * assumée d'une règle unique — et une facture archivée par erreur se restaure.
 *
 * Une seule fonction porte cette règle. Toutes les autres s'y réfèrent.
 */
export const STATUT_ARCHIVEE = 'Archivée';

export function estArchive(doc: DocumentComptable | null | undefined): boolean {
  return doc?.statut === STATUT_ARCHIVEE;
}

/**
 * Ce document appartient-il encore à l'activité ?
 *
 * C'est le premier filtre de tout calcul métier. Ce qui est archivé n'y entre
 * pas — quel que soit le reste.
 */
export function entreDansLActivite(doc: DocumentComptable | null | undefined): boolean {
  return !estArchive(doc);
}

/**
 * Ce document entre-t-il dans le chiffre d'affaires ?
 *
 * Un brouillon n'est pas émis, une facture annulée n'existe plus, une facture
 * archivée est sortie de l'activité. Tout le reste compte — un avoir compris,
 * et négativement. Sa résolution n'entre pas dans cette décision : la règle est
 * « dès l'émission ».
 */
export function entreDansLeCA(doc: DocumentComptable | null | undefined): boolean {
  if (!entreDansLActivite(doc)) return false;
  return doc?.statut !== 'Brouillon' && doc?.statut !== 'Annulée';
}

/**
 * Ce document compte-t-il dans le « Total affiché » de la liste des factures ?
 *
 * Amine, 19 septembre 2026 : la 20231257 (Mondial Relay), au statut
 * « Annulée » sans avoir, gonflait encore le total de mars de 829,18 €. Une
 * facture annulée n'existe plus : elle reste visible dans la liste, mais ne
 * s'additionne pas. Les brouillons, eux, restent comptés — le total de la
 * liste dit ce qui est prévu, pas seulement ce qui est émis.
 */
export function compteDansLeTotalAffiche(doc: DocumentComptable | null | undefined): boolean {
  if (!entreDansLActivite(doc)) return false;
  return doc?.statut !== 'Annulée';
}

export function separerParNature<T extends DocumentComptable>(
  docs: T[]
): { factures: T[]; avoirs: T[] } {
  const factures: T[] = [];
  const avoirs: T[] = [];
  for (const doc of docs) (estAvoir(doc) ? avoirs : factures).push(doc);
  return { factures, avoirs };
}

/** Un avoir reste à traiter tant qu'il n'a été ni remboursé ni imputé. */
export function resteATraiter(avoir: DocumentComptable | null | undefined): boolean {
  if (!estAvoir(avoir)) return false;
  return (avoir?.resolution || RESOLUTION_EN_ATTENTE) === RESOLUTION_EN_ATTENTE;
}

export interface CompteursAvoirs {
  nombre: number;
  montantTTC: number;
  montantHT: number;
  nombreATraiter: number;
  montantATraiter: number;
}

/**
 * Les compteurs de la vue des avoirs. Les montants y sont rendus POSITIFS :
 * c'est un décompte d'avoirs, pas une contribution au chiffre d'affaires. Le
 * signe appartient aux agrégats, et à eux seuls.
 */
export function compteursAvoirs(docs: DocumentComptable[]): CompteursAvoirs {
  const { avoirs } = separerParNature(docs);
  const emis = avoirs.filter(entreDansLeCA);
  const aTraiter = emis.filter(resteATraiter);

  return {
    nombre: emis.length,
    montantTTC: arrondirCentimes(emis.reduce((s, a) => s + (Number(a.totalTTC) || 0), 0)),
    montantHT: arrondirCentimes(emis.reduce((s, a) => s + (Number(a.totalHT) || 0), 0)),
    nombreATraiter: aTraiter.length,
    montantATraiter: arrondirCentimes(aTraiter.reduce((s, a) => s + (Number(a.totalTTC) || 0), 0)),
  };
}

export interface ChiffreAffaires {
  /** Ce que les factures seules totalisent. */
  brutFactures: number;
  /** Ce que les avoirs retranchent, en valeur absolue. */
  avoirs: number;
  /** Le chiffre d'affaires réel : factures moins avoirs. */
  net: number;
}

/**
 * Le chiffre d'affaires d'un ensemble de documents, avoirs déduits.
 *
 * Rend les trois nombres, et non le seul résultat : une correction qu'on ne
 * peut pas expliquer n'est pas vérifiable.
 */
export function chiffreAffaires(docs: DocumentComptable[]): ChiffreAffaires {
  const retenus = docs.filter(entreDansLeCA);
  const { factures, avoirs } = separerParNature(retenus);

  const brutFactures = arrondirCentimes(
    factures.reduce((s, f) => s + (Number(f.totalTTC) || 0), 0)
  );
  const montantAvoirs = arrondirCentimes(
    avoirs.reduce((s, a) => s + (Number(a.totalTTC) || 0), 0)
  );

  return {
    brutFactures,
    avoirs: montantAvoirs,
    net: arrondirCentimes(brutFactures - montantAvoirs),
  };
}

// ===========================================================================
// L'ÉTAT D'AFFICHAGE — ce qu'on MONTRE, jamais ce qu'on enregistre
// ===========================================================================
//
// Une facture intégralement avoirée reste « Envoyée » en base : c'est ce qui
// s'est passé, et l'historique d'un document comptable ne se réécrit pas. Mais
// « Envoyée » est trompeur à l'écran quand un avoir l'a annulée.
//
// D'où cet état CALCULÉ, dérivé des avoirs liés à chaque instant. Il ne
// remplace le statut qu'à l'affichage ; la colonne « statut » n'est jamais
// touchée, et le statut d'origine reste rendu par « statutOrigine » pour être
// montré dans le détail ou l'historique.
//
// IL NE DÉDUIT RIEN. Le chiffre d'affaires est calculé par « chiffreAffaires »,
// qui retranche chaque avoir UNE fois, à sa propre date d'émission. Rien ici
// n'entre dans ce calcul — c'est vérifié par les tests, parce que c'est
// précisément l'erreur qu'on vient de corriger et qu'il serait facile de
// refaire à l'envers.

export const ETAT_PARTIELLEMENT_AVOIREE = 'Partiellement avoirée';
export const ETAT_ANNULEE_PAR_AVOIR = 'Annulée par avoir';

export interface EtatAffichage {
  /** Ce qu'on affiche dans la colonne « Statut ». */
  libelle: string;
  /** Ce que dit la base. Jamais modifié, toujours consultable. */
  statutOrigine: string;
  /** L'affichage diffère-t-il du statut enregistré ? */
  remplace: boolean;
  /** Total des avoirs liés, en valeur absolue. */
  montantAvoire: number;
  /** Ce qu'il reste dû sur cette facture, plancher à zéro. */
  resteDu: number;
}

/** Le total des avoirs liés qui comptent — un avoir en brouillon n'annule rien. */
export function montantAvoire(avoirsLies: DocumentComptable[]): number {
  return arrondirCentimes(
    avoirsLies
      .filter((a) => estAvoir(a) && entreDansLeCA(a))
      .reduce((somme, a) => somme + (Number(a.totalTTC) || 0), 0)
  );
}

/**
 * Le net d'une facture, ses avoirs déduits.
 *
 * Sert aux TOTAUX DE LISTE — le total mensuel de la liste des factures, où les
 * avoirs n'apparaissent pas et où leur effet doit pourtant se voir. Il ne sert
 * pas au chiffre d'affaires, qui a son propre calcul.
 */
export function netDeLaFacture(
  facture: DocumentComptable,
  avoirsLies: DocumentComptable[]
): number {
  return arrondirCentimes((Number(facture?.totalTTC) || 0) - montantAvoire(avoirsLies));
}

export const ETAT_RETARD = 'Retard';

/**
 * L'échéance est-elle dépassée à l'instant donné ? Comparaison au JOUR : une
 * facture échue « le 18 juillet » n'est en retard qu'à partir du 19.
 */
export function echeanceDepassee(facture: DocumentComptable, maintenant: Date): boolean {
  const brute = facture?.echeance ?? facture?.dateEcheance;
  if (!brute) return false;
  const echeance = new Date(brute);
  if (Number.isNaN(echeance.getTime())) return false;
  const finDuJour = new Date(echeance.getFullYear(), echeance.getMonth(), echeance.getDate(), 23, 59, 59, 999);
  return maintenant.getTime() > finDuJour.getTime();
}

/**
 * L'état affiché d'une facture : ses avoirs d'abord, puis le RETARD.
 *
 * Amine, 19 septembre 2026 : « pour Euromedmultimedia je vois des factures
 * toujours non payées mais elles ne s'affichent pas en retard, pourquoi ? ».
 * Parce que rien ne les y mettait : la routine qui écrivait « Retard » en
 * base n'était appelée nulle part. Comme pour les avoirs, le retard devient
 * un état CALCULÉ — une facture émise (Envoyée, Téléchargée), échue à
 * l'instant donné et encore due s'affiche « Retard ». La colonne « statut »
 * n'est jamais touchée ; sans `maintenant`, la règle ne s'applique pas.
 */
export function etatAffichage(
  facture: DocumentComptable,
  avoirsLies: DocumentComptable[] = [],
  maintenant?: Date
): EtatAffichage {
  const statutOrigine = facture?.statut || '';
  const avoire = montantAvoire(avoirsLies);
  const montant = Number(facture?.totalTTC) || 0;

  if (avoire <= 0) {
    const resteDu = arrondirCentimes(montant);
    const enRetard = !!maintenant && !estAvoir(facture)
      && (statutOrigine === 'Envoyée' || statutOrigine === 'Téléchargée')
      && resteDu > 0 && echeanceDepassee(facture, maintenant);
    return {
      libelle: enRetard ? ETAT_RETARD : statutOrigine,
      statutOrigine, remplace: enRetard, montantAvoire: 0, resteDu,
    };
  }

  // La tolérance d'un centime évite qu'un arrondi fasse passer une couverture
  // totale pour partielle — 17 697,192 € avoirés sur 17 697,192 € facturés.
  const totale = avoire >= montant - 0.005;

  return {
    libelle: totale ? ETAT_ANNULEE_PAR_AVOIR : ETAT_PARTIELLEMENT_AVOIREE,
    statutOrigine,
    remplace: true,
    montantAvoire: avoire,
    resteDu: arrondirCentimes(Math.max(0, montant - avoire)),
  };
}

/**
 * Le filtre de statut de la liste des factures porte sur l'ÉTAT AFFICHÉ, et non
 * sur la colonne « statut ».
 *
 * Sans cela, une facture intégralement avoirée — affichée « Annulée par avoir »
 * — continuerait de sortir sous le filtre « Envoyée », puisque c'est ce que dit
 * encore la base. La liste montrerait alors, sous « Envoyée », des lignes qui
 * n'affichent pas « Envoyée ». Un filtre doit trouver ce que la colonne montre.
 *
 * Les deux états d'avoir deviennent du même coup des filtres à part entière :
 * « Partiellement avoirée » et « Annulée par avoir » se retrouvent comme les
 * autres. La colonne « statut » n'est toujours pas touchée.
 */
export function correspondAuFiltre(
  facture: DocumentComptable,
  avoirsLies: DocumentComptable[],
  filtre: string | null | undefined,
  maintenant?: Date
): boolean {
  if (!filtre || filtre === 'ALL') return true;
  return etatAffichage(facture, avoirsLies, maintenant).libelle === filtre;
}

/** Les états d'avoir, offerts au filtre au même titre que les statuts. */
export const FILTRES_ETAT_AVOIR = [ETAT_PARTIELLEMENT_AVOIREE, ETAT_ANNULEE_PAR_AVOIR] as const;

/**
 * Dans quelle série du graphique « Évolution des ventes » ce document tombe-t-il ?
 *
 * Un avoir a la SIENNE. Le fondre dans « Non encaissé (estimation) » — ce que
 * faisait le code, puisqu'un avoir porte le statut « Envoyée » — a produit un
 * « Non encaissé » de −34 053,19 € en mai 2026 : une barre négative sous une
 * étiquette qui annonce des recettes à venir. Le nombre était juste, la série
 * ne l'était pas.
 *
 * Rend « null » pour ce qui n'est représenté nulle part (« Annulée »,
 * « Archivée », « Téléchargée »).
 */
export type SerieGraphique = 'avoirs' | 'factures' | 'estimation' | null;

export function serieDuDocument(doc: DocumentComptable | null | undefined): SerieGraphique {
  if (!doc) return null;
  if (!entreDansLActivite(doc)) return null;
  if (estAvoir(doc)) return 'avoirs';
  if (doc.statut === 'Payée') return 'factures';
  // « Téléchargée » manquait : quatorze factures de production n'apparaissaient
  // sur aucun graphique.
  if (doc.statut === 'Brouillon' || estEmise(doc)) return 'estimation';
  return null;
}

// ===========================================================================
// LES SOLDES — ce qui reste RÉELLEMENT dû
// ===========================================================================
//
// Une facture intégralement avoirée n'est plus une créance : personne ne la
// paiera. La compter parmi les impayés, les retards ou les échéances à venir
// présente comme dû ce qui est déjà compensé — c'est ce qui affichait
// 33 613,27 € de créances imaginaires chez Eco Delivery Urban.
//
// L'index et le solde vivent ICI, et sont les seuls de l'application : les
// rapports, le tableau de bord et la liste des factures s'en servent tous.

export interface DocumentAvecLien extends DocumentComptable {
  id?: string;
  factureOriginaleId?: string | null;
}

/** Les avoirs d'un ensemble, rangés sous l'identifiant de la facture qu'ils visent. */
export function indexerAvoirsParFacture<T extends DocumentAvecLien>(
  documents: T[]
): Map<string, T[]> {
  const index = new Map<string, T[]>();
  for (const doc of documents || []) {
    if (!estAvoir(doc) || !doc.factureOriginaleId) continue;
    const liste = index.get(doc.factureOriginaleId) || [];
    liste.push(doc);
    index.set(doc.factureOriginaleId, liste);
  }
  return index;
}

/** Ce qu'il reste dû sur une facture : son montant, moins les avoirs qui la visent. */
export function soldeDeLaFacture(
  facture: DocumentAvecLien,
  avoirsParFacture: Map<string, DocumentAvecLien[]>
): number {
  const avoire = montantAvoire(avoirsParFacture.get(facture?.id || '') || []);
  return arrondirCentimes(Math.max(0, (Number(facture?.totalTTC) || 0) - avoire));
}

/**
 * Les statuts d'une facture ÉMISE — sortie chez le client.
 *
 * « Téléchargée » en fait partie : une facture qu'on a produite en PDF et
 * remise au client est émise, même si aucun courriel n'est parti depuis
 * l'application. Elles sont quatorze en production, dont quatorze échues.
 *
 * « Brouillon » n'en est PAS. Un brouillon n'a pas quitté l'application : il ne
 * peut donc pas être en retard de paiement, et le compter comme tel présentait
 * 6 714,44 € de créances qui n'ont jamais été réclamées à personne.
 */
export const STATUTS_FACTURE_EMISE = ['Envoyée', 'Téléchargée', 'Retard'] as const;

export function estEmise(doc: DocumentComptable | null | undefined): boolean {
  return STATUTS_FACTURE_EMISE.includes(doc?.statut as any);
}

/**
 * Cette facture constitue-t-elle encore une créance ?
 *
 * Quatre cas où la réponse est non, et un seul endroit pour les dire :
 *
 *  - un AVOIR n'est pas une créance, c'est l'inverse ;
 *  - une facture qui n'a pas été ÉMISE non plus : un brouillon n'a rien
 *    réclamé à personne, il ne peut pas être en retard ;
 *  - une facture RÉGLÉE ou ANNULÉE ne se relance pas — ni une ARCHIVÉE, sortie
 *    de l'activité ;
 *  - une facture dont un avoir a soldé le montant ne doit plus rien.
 */
export function estEncoreDue(
  facture: DocumentAvecLien,
  avoirsParFacture: Map<string, DocumentAvecLien[]>
): boolean {
  if (estAvoir(facture)) return false;
  if (!entreDansLActivite(facture)) return false;
  // « estEmise » écarte d'un coup les brouillons, les payées et les annulées :
  // aucun de ces statuts n'y figure.
  if (!estEmise(facture)) return false;
  return soldeDeLaFacture(facture, avoirsParFacture) > 0;
}

/**
 * L'IMPUTATION des avoirs sur les factures d'un ensemble.
 *
 * Un avoir se retranche UNE fois, et une seule. Deux voies, jamais les deux :
 *
 *  - il est rattaché à une facture de l'ensemble → il allège son SOLDE, et
 *    c'est par là qu'il pèse sur les totaux ;
 *  - il ne l'est pas — non rattaché, rattaché hors de l'ensemble, ou dépassant
 *    le montant de sa facture → sa part non imputée est retranchée à part.
 *
 * Sans la seconde voie, un avoir orphelin disparaîtrait purement et simplement
 * des calculs. Sans la première, une facture intégralement avoirée resterait
 * comptée comme une créance.
 */
export interface Imputation {
  /** Ce qu'il reste dû sur une facture. */
  solde(facture: DocumentAvecLien): number;
  /** La part d'un avoir qu'aucune facture de l'ensemble n'absorbe. */
  partNonImputee(avoir: DocumentAvecLien): number;
  /** Cette facture constitue-t-elle encore une créance ? */
  estEncoreDue(facture: DocumentAvecLien): boolean;
}

export function imputer(documents: DocumentAvecLien[]): Imputation {
  const avoirsParFacture = indexerAvoirsParFacture(documents || []);
  const facturesParId = new Map<string, DocumentAvecLien>();
  for (const doc of documents || []) {
    if (!estAvoir(doc) && doc.id) facturesParId.set(doc.id, doc);
  }

  const solde = (facture: DocumentAvecLien) => soldeDeLaFacture(facture, avoirsParFacture);

  const partNonImputee = (avoir: DocumentAvecLien): number => {
    if (!estAvoir(avoir)) return 0;
    const montant = Number(avoir.totalTTC) || 0;
    const origine = avoir.factureOriginaleId ? facturesParId.get(avoir.factureOriginaleId) : null;
    if (!origine) return arrondirCentimes(montant);

    const cumul = montantAvoire(avoirsParFacture.get(origine.id || '') || []);
    const absorbable = Number(origine.totalTTC) || 0;
    if (cumul <= absorbable || cumul <= 0) return 0;
    // L'excédent se répartit entre les avoirs de la facture, au prorata.
    return arrondirCentimes((montant / cumul) * (cumul - absorbable));
  };

  return {
    solde,
    partNonImputee,
    estEncoreDue: (facture) => estEncoreDue(facture, avoirsParFacture),
  };
}
